import Box from '@mui/material/Box';
import TextField from '@material-ui/core/TextField';
import createStyles from '@material-ui/core/styles/createStyles';
import { Theme } from '@material-ui/core/styles/createTheme';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import InputAdornment from '@material-ui/core/InputAdornment';
import ClearIcon from '@material-ui/icons/Clear';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setNewAppSlug, _newAppSlug } from '../../store/slices/editorSlice';
import {
	changeAppBodyColor,
	changeNavbarBackgroundColor,
	changeNavbarItemColor,
	changeNavbarTextColor,
	changeIsGlassy,
	changeGlassTintColor,
	changeGlassGradientColor1,
	changeGlassGradientColor2,
	changeGlassGradientAngle,
	changeGlassTextColor,
	changeImageRound,
	saveLocation,
	_selectedLocation,
} from '../../store/slices/locationSlice';
import { saveAppSlug, _selectedOrganization } from '../../store/slices/organizationSlice';
import { FlatButton } from '../Buttons/FlatButton';
import { ViiisionTempDrawer } from '../ViiisionTempDrawer';
import { QRColorPickerPopover } from '../Dashboard/QREditor/QRColorPickerPopover';

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		text: {
			// width: 264,
			marginBottom: 20,
		},
		content: {
			display: 'flex',
			justifyContent: 'space-between',
			flexDirection: 'column',
			width: 557,
			height: '75%',
			marginLeft: 10,
		},
		buttonBox: {
			marginTop: 40,
			marginBottom: 75,
			display: 'flex',
			justifyContent: 'flex-end',
			alignItems: 'center',
			height: 80,
		},
		button: {
			height: 40,
			marginRight: 10,
		},
		formSection: {
			marginBottom: 24,
		},
		formField: {
			marginBottom: 16,
			width: '100%',
		},
		sectionTitle: {
			marginBottom: 12,
			fontWeight: 600,
		},
		readOnlyField: {
			'& .MuiInputBase-input': {
				color: theme.palette.text.secondary,
			},
		},
		colorRow: {
			display: 'flex',
			alignItems: 'flex-start',
			gap: 12,
		},
		colorSwatch: {
			width: 56,
			height: 56,
			borderRadius: 4,
			border: '1px solid #ccc',
			flexShrink: 0,
			marginTop: 0,
		},
	})
);

export type AppInformationDrawerProps = {
	open: boolean;
	setOpen: (open: boolean) => void;
};

// App Information Drawer component
// Similar to MLDrawer, this drawer allows users to edit app information
// The main feature is the app slug which will be saved to the organization collection
// Additional app information is displayed for context and to make use of the space
export const AppInformationDrawer = (props: AppInformationDrawerProps) => {
	const { open, setOpen } = { ...props };
	const classes = useStyles();
	const dispatch = useAppDispatch();
	const selectedLocation = useAppSelector(_selectedLocation);
	const selectedOrganization = useAppSelector(_selectedOrganization);
	const newAppSlug = useAppSelector(_newAppSlug);

	const saveAppSlugAction = bindActionCreators(saveAppSlug, dispatch);
	const updateNewAppSlug = bindActionCreators(setNewAppSlug, dispatch);
	const [enableButton, setEnableButton] = useState(false);

	// Initialize form data from location and organization
	const [formData, setFormData] = useState({
		appSlug: '',
		appName: '',
		companyName: '',
		contactName: '',
		phoneNumber: '',
		email: '',
		organizationEmail: '',
		subscriptionStatus: '',
	});

	// Error state for app slug validation
	const [appSlugError, setAppSlugError] = useState('');

	// Color states
	const [appBodyColor, setAppBodyColor] = useState('');
	const [colorError, setColorError] = useState('');
	const [navbarBgColor, setNavbarBgColor] = useState('');
	const [navbarBgError, setNavbarBgError] = useState('');
	const [navbarItemColor, setNavbarItemColor] = useState('');
	const [navbarItemError, setNavbarItemError] = useState('');
	const [navbarTextColor, setNavbarTextColor] = useState('');
	const [navbarTextError, setNavbarTextError] = useState('');
	const [isGlassy, setIsGlassy] = useState(false);
	const [glassTintColor, setGlassTintColor] = useState('');
	const [gradientColor1, setGradientColor1] = useState('');
	const [gradientColor2, setGradientColor2] = useState('');
	const [gradientAngle, setGradientAngle] = useState('');
	const [glassTextColor, setGlassTextColor] = useState('');
	const [imageRound, setImageRound] = useState(false);

	// Initialize form data when drawer opens or data changes
	useEffect(() => {
		if (open && selectedLocation && selectedOrganization) {
			const currentSlug = newAppSlug !== null ? newAppSlug : selectedOrganization?.appSlug || '';
			setFormData({
				appSlug: currentSlug,
				appName: selectedLocation?.name || '',
				companyName: selectedLocation?.companyName || selectedOrganization?.name || '',
				contactName: selectedLocation?.contactName || '',
				phoneNumber: selectedLocation?.phoneNumber || '',
				email: selectedLocation?.email || '',
				organizationEmail: selectedOrganization?.email || '',
				subscriptionStatus: selectedOrganization?.subscription_active ? 'Active' : 'Inactive',
			});
			// Clear error when drawer opens
			setAppSlugError('');
			setAppBodyColor(selectedLocation?.appBodyColor || '');
			setColorError('');
			setNavbarBgColor(selectedLocation?.navbarBackgroundColor || '');
			setNavbarBgError('');
			setNavbarItemColor(selectedLocation?.navbarItemColor || '');
			setNavbarItemError('');
			setNavbarTextColor(selectedLocation?.navbarTextColor || '');
			setNavbarTextError('');
			setIsGlassy(selectedLocation?.isGlassy || false);
			setGlassTintColor(selectedLocation?.glassTintColor || '');
			setGradientColor1(selectedLocation?.glassGradientColor1 || '');
			setGradientColor2(selectedLocation?.glassGradientColor2 || '');
			setGradientAngle(selectedLocation?.glassGradientAngle || '');
			setGlassTextColor(selectedLocation?.glassTextColor || '');
			setImageRound(selectedLocation?.imageRound || false);
		}
	}, [open, selectedLocation, selectedOrganization, newAppSlug]);

	// Initialize newAppSlug when drawer opens
	useEffect(() => {
		if (open && selectedOrganization && newAppSlug === null) {
			updateNewAppSlug(selectedOrganization?.appSlug || '');
		}
	}, [open, selectedOrganization, newAppSlug, updateNewAppSlug]);

	const handleSlugChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		// Sanitize slug: only allow lowercase letters, numbers, and hyphens
		const sanitized = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
		setFormData((prev) => ({ ...prev, appSlug: sanitized }));
		updateNewAppSlug(sanitized);
		setEnableButton(selectedOrganization?.appSlug !== sanitized || hasAnyColorChanged());
		// Clear error when user starts typing
		setAppSlugError('');
	};

	const isValidHex = (value: string) => /^[0-9a-fA-F]{6}([0-9a-fA-F]{2})?$/.test(value);

	// Check if any color field has changed from its original location value
	const hasAnyColorChanged = (overrides: Record<string, any> = {}) => {
		const vals = {
			appBodyColor: overrides.appBodyColor ?? appBodyColor,
			navbarBgColor: overrides.navbarBgColor ?? navbarBgColor,
			navbarItemColor: overrides.navbarItemColor ?? navbarItemColor,
			navbarTextColor: overrides.navbarTextColor ?? navbarTextColor,
			isGlassy: overrides.isGlassy ?? isGlassy,
			glassTintColor: overrides.glassTintColor ?? glassTintColor,
			glassGradientColor1: overrides.glassGradientColor1 ?? gradientColor1,
			glassGradientColor2: overrides.glassGradientColor2 ?? gradientColor2,
			glassGradientAngle: overrides.glassGradientAngle ?? gradientAngle,
			glassTextColor: overrides.glassTextColor ?? glassTextColor,
			imageRound: overrides.imageRound ?? imageRound,
		};
		return (
			vals.appBodyColor !== (selectedLocation?.appBodyColor || '') ||
			vals.navbarBgColor !== (selectedLocation?.navbarBackgroundColor || '') ||
			vals.navbarItemColor !== (selectedLocation?.navbarItemColor || '') ||
			vals.navbarTextColor !== (selectedLocation?.navbarTextColor || '') ||
			vals.isGlassy !== (selectedLocation?.isGlassy || false) ||
			vals.glassTintColor !== (selectedLocation?.glassTintColor || '') ||
			vals.glassGradientColor1 !== (selectedLocation?.glassGradientColor1 || '') ||
			vals.glassGradientColor2 !== (selectedLocation?.glassGradientColor2 || '') ||
			vals.glassGradientAngle !== (selectedLocation?.glassGradientAngle || '') ||
			vals.glassTextColor !== (selectedLocation?.glassTextColor || '') ||
			vals.imageRound !== (selectedLocation?.imageRound || false)
		);
	};

	const hasSlugChanged = () => selectedOrganization?.appSlug !== formData.appSlug;

	const updateEnableButton = (overrides: Record<string, any> = {}) => {
		setEnableButton(hasAnyColorChanged(overrides) || hasSlugChanged());
	};

	// Generic color change handler factory
	const makeColorHandler = (setter: (v: string) => void, errorSetter: (v: string) => void, fieldKey: string) => ({
		onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
			const raw = event.target.value.replace(/^#/, '');
			const sanitized = raw.replace(/[^0-9a-fA-F]/g, '').slice(0, 8);
			setter(sanitized);
			if (sanitized === '' || isValidHex(sanitized)) {
				errorSetter('');
				updateEnableButton({ [fieldKey]: sanitized });
			} else {
				errorSetter('Must be a valid hex color — 6 chars (445854) or 8 with alpha (445854CC)');
			}
		},
		onClear: () => {
			setter('');
			errorSetter('');
			updateEnableButton({ [fieldKey]: '' });
		},
	});

	const bodyColorHandlers = makeColorHandler(setAppBodyColor, setColorError, 'appBodyColor');
	const navBgHandlers = makeColorHandler(setNavbarBgColor, setNavbarBgError, 'navbarBgColor');
	const navItemHandlers = makeColorHandler(setNavbarItemColor, setNavbarItemError, 'navbarItemColor');
	const navTextHandlers = makeColorHandler(setNavbarTextColor, setNavbarTextError, 'navbarTextColor');

	const handleCancel = () => {
		updateNewAppSlug(null);
		setAppSlugError('');
		setOpen(false);
	};

	const handleSave = async () => {
		setAppSlugError('');
		try {
			// Save colors immediately — dispatch updates Redux, then saveLocation triggers middleware to persist
			let colorChanged = false;
			if (
				(appBodyColor === '' || isValidHex(appBodyColor)) &&
				appBodyColor !== (selectedLocation?.appBodyColor || '')
			) {
				dispatch(changeAppBodyColor(appBodyColor));
				colorChanged = true;
			}
			if (
				(navbarBgColor === '' || isValidHex(navbarBgColor)) &&
				navbarBgColor !== (selectedLocation?.navbarBackgroundColor || '')
			) {
				dispatch(changeNavbarBackgroundColor(navbarBgColor));
				colorChanged = true;
			}
			if (
				(navbarItemColor === '' || isValidHex(navbarItemColor)) &&
				navbarItemColor !== (selectedLocation?.navbarItemColor || '')
			) {
				dispatch(changeNavbarItemColor(navbarItemColor));
				colorChanged = true;
			}
			if (
				(navbarTextColor === '' || isValidHex(navbarTextColor)) &&
				navbarTextColor !== (selectedLocation?.navbarTextColor || '')
			) {
				dispatch(changeNavbarTextColor(navbarTextColor));
				colorChanged = true;
			}
			if (isGlassy !== (selectedLocation?.isGlassy || false)) {
				dispatch(changeIsGlassy(isGlassy));
				colorChanged = true;
			}
			if (
				(glassTintColor === '' || isValidHex(glassTintColor)) &&
				glassTintColor !== (selectedLocation?.glassTintColor || '')
			) {
				dispatch(changeGlassTintColor(glassTintColor));
				colorChanged = true;
			}
			if (
				(gradientColor1 === '' || isValidHex(gradientColor1)) &&
				gradientColor1 !== (selectedLocation?.glassGradientColor1 || '')
			) {
				dispatch(changeGlassGradientColor1(gradientColor1));
				colorChanged = true;
			}
			if (
				(gradientColor2 === '' || isValidHex(gradientColor2)) &&
				gradientColor2 !== (selectedLocation?.glassGradientColor2 || '')
			) {
				dispatch(changeGlassGradientColor2(gradientColor2));
				colorChanged = true;
			}
			if (gradientAngle !== (selectedLocation?.glassGradientAngle || '')) {
				dispatch(changeGlassGradientAngle(gradientAngle));
				colorChanged = true;
			}
			if (
				(glassTextColor === '' || isValidHex(glassTextColor)) &&
				glassTextColor !== (selectedLocation?.glassTextColor || '')
			) {
				dispatch(changeGlassTextColor(glassTextColor));
				colorChanged = true;
			}
			if (imageRound !== (selectedLocation?.imageRound || false)) {
				dispatch(changeImageRound(imageRound));
				colorChanged = true;
			}
			if (colorChanged) {
				dispatch(saveLocation());
			}

			// Save slug immediately if it changed
			if (selectedOrganization?.appSlug !== formData.appSlug) {
				await saveAppSlugAction();
			}

			setEnableButton(false);
			setOpen(false);
		} catch (error: any) {
			// Error is shown via toast by axios interceptor
			// Extract and show the actual error message inline
			let errorMessage = 'An error occurred while saving. Please try again.';

			if (error?.response?.data) {
				const responseData = error.response.data;
				if (typeof responseData === 'string') {
					// Extract the error message before the pipe character
					const messageParts = responseData.split(' | ');
					if (messageParts.length > 0 && messageParts[0].trim()) {
						errorMessage = messageParts[0].trim();
					}
				} else if (typeof responseData === 'object' && responseData.message) {
					errorMessage = responseData.message;
				}
			} else if (error?.message) {
				errorMessage = error.message;
			}

			setAppSlugError(errorMessage);
			// Keep drawer open so user can fix and try again
		}
	};

	return (
		<ViiisionTempDrawer open={open} setOpen={setOpen} title={'App Information'}>
			<Box className={classes.content}>
				<Typography className={classes.text} variant="body2">
					Manage your app's information and settings. The app slug is used in your app's URL and can be
					customized to create a unique identifier for your app.
				</Typography>

				{/* App Slug Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle} variant="subtitle2">
						APP SLUG
					</Typography>
					<TextField
						className={classes.formField}
						label="App Slug"
						value={formData.appSlug}
						onChange={handleSlugChange}
						placeholder="my-app-slug"
						helperText={
							appSlugError ||
							"Used in your app's URL. Only lowercase letters, numbers, and hyphens are allowed."
						}
						error={!!appSlugError}
						variant="outlined"
						fullWidth
					/>
				</Box>

				{/* App Body Color Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle} variant="subtitle2">
						APP BODY COLOR
					</Typography>
					<Box className={classes.colorRow}>
						<Box
							className={classes.colorSwatch}
							style={{
								backgroundColor:
									appBodyColor && isValidHex(appBodyColor) ? `#${appBodyColor}` : '#ffffff',
							}}
						/>
						<TextField
							className={classes.formField}
							label="Hex Color"
							value={appBodyColor}
							onChange={bodyColorHandlers.onChange}
							placeholder="445854 or 445854CC"
							helperText={
								colorError ||
								'Hex color for app body background. 6 chars, or 8 with alpha (e.g. 445854CC). Leave empty for default.'
							}
							error={!!colorError}
							variant="outlined"
							fullWidth
							InputProps={{
								startAdornment: <InputAdornment position="start">#</InputAdornment>,
								endAdornment: appBodyColor ? (
									<InputAdornment position="end">
										<IconButton size="small" onClick={bodyColorHandlers.onClear}>
											<ClearIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								) : null,
							}}
							inputProps={{ maxLength: 8 }}
						/>
					</Box>
				</Box>

				{/* Navbar Styles Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle} variant="subtitle2">
						NAVBAR STYLES
					</Typography>
					{/* Navbar Background Color */}
					<Box className={classes.colorRow}>
						<Box
							className={classes.colorSwatch}
							style={{
								backgroundColor:
									navbarBgColor && isValidHex(navbarBgColor) ? `#${navbarBgColor}` : '#F6F6F7',
							}}
						/>
						<TextField
							className={classes.formField}
							label="NavBar Background Color"
							value={navbarBgColor}
							onChange={navBgHandlers.onChange}
							placeholder="F6F6F7"
							helperText={
								navbarBgError || 'Background color of the navigation bar. Leave empty for default.'
							}
							error={!!navbarBgError}
							variant="outlined"
							fullWidth
							InputProps={{
								startAdornment: <InputAdornment position="start">#</InputAdornment>,
								endAdornment: navbarBgColor ? (
									<InputAdornment position="end">
										<IconButton size="small" onClick={navBgHandlers.onClear}>
											<ClearIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								) : null,
							}}
							inputProps={{ maxLength: 6 }}
						/>
					</Box>
					{/* Navbar Item Color */}
					<Box className={classes.colorRow}>
						<Box
							className={classes.colorSwatch}
							style={{
								backgroundColor:
									navbarItemColor && isValidHex(navbarItemColor) ? `#${navbarItemColor}` : '#FFFFFF',
							}}
						/>
						<TextField
							className={classes.formField}
							label="NavBar Item Color"
							value={navbarItemColor}
							onChange={navItemHandlers.onChange}
							placeholder="FFFFFF"
							helperText={
								navbarItemError || 'Background color of dropdown items. Leave empty for default.'
							}
							error={!!navbarItemError}
							variant="outlined"
							fullWidth
							InputProps={{
								startAdornment: <InputAdornment position="start">#</InputAdornment>,
								endAdornment: navbarItemColor ? (
									<InputAdornment position="end">
										<IconButton size="small" onClick={navItemHandlers.onClear}>
											<ClearIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								) : null,
							}}
							inputProps={{ maxLength: 6 }}
						/>
					</Box>
					{/* Navbar Text Color */}
					<Box className={classes.colorRow}>
						<Box
							className={classes.colorSwatch}
							style={{
								backgroundColor:
									navbarTextColor && isValidHex(navbarTextColor) ? `#${navbarTextColor}` : '#49525D',
							}}
						/>
						<TextField
							className={classes.formField}
							label="NavBar Text Color"
							value={navbarTextColor}
							onChange={navTextHandlers.onChange}
							placeholder="49525D"
							helperText={
								navbarTextError || 'Text and icon color in the navigation bar. Leave empty for default.'
							}
							error={!!navbarTextError}
							variant="outlined"
							fullWidth
							InputProps={{
								startAdornment: <InputAdornment position="start">#</InputAdornment>,
								endAdornment: navbarTextColor ? (
									<InputAdornment position="end">
										<IconButton size="small" onClick={navTextHandlers.onClear}>
											<ClearIcon fontSize="small" />
										</IconButton>
									</InputAdornment>
								) : null,
							}}
							inputProps={{ maxLength: 6 }}
						/>
					</Box>
				</Box>

				{/* Glassy Mode Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle} variant="subtitle2">
						GLASSY MODE
					</Typography>
					<FormControlLabel
						control={
							<Switch
								checked={isGlassy}
								onChange={(e) => {
									setIsGlassy(e.target.checked);
									updateEnableButton({ isGlassy: e.target.checked });
								}}
								color="primary"
							/>
						}
						label="Enable glassy UI"
					/>
					{isGlassy && (
						<>
							<>
								<Box style={{ marginTop: 12 }}>
									<QRColorPickerPopover
										name="glassTintColor"
										label="Glass Tint Color"
										color={glassTintColor ? `#${glassTintColor}` : '#FFFFFF'}
										setColor={(color: string) => {
											const hex = color.replace(/^#/, '');
											setGlassTintColor(hex);
											updateEnableButton({ glassTintColor: hex });
										}}
										setError={() => {}}
									/>
								</Box>
								<Box style={{ marginTop: 12 }}>
									<QRColorPickerPopover
										name="gradientColor1"
										label="Gradient Color 1"
										color={gradientColor1 ? `#${gradientColor1}` : '#1a1a2e'}
										setColor={(color: string) => {
											const hex = color.replace(/^#/, '');
											setGradientColor1(hex);
											updateEnableButton({ glassGradientColor1: hex });
										}}
										setError={() => {}}
									/>
								</Box>
								<Box style={{ marginTop: 12 }}>
									<QRColorPickerPopover
										name="gradientColor2"
										label="Gradient Color 2"
										color={gradientColor2 ? `#${gradientColor2}` : '#1a1a2e'}
										setColor={(color: string) => {
											const hex = color.replace(/^#/, '');
											setGradientColor2(hex);
											updateEnableButton({ glassGradientColor2: hex });
										}}
										setError={() => {}}
									/>
								</Box>
								<TextField
									className={classes.formField}
									label="Gradient Angle"
									value={gradientAngle}
									onChange={(e) => {
										const val = e.target.value.replace(/[^0-9]/g, '');
										const num = parseInt(val, 10);
										if (val === '' || (num >= 0 && num <= 360)) {
											setGradientAngle(val);
											updateEnableButton({ glassGradientAngle: val });
										}
									}}
									placeholder="135"
									helperText="Angle in degrees (0-360). Leave empty for default 135°."
									variant="outlined"
									fullWidth
									style={{ marginTop: 12 }}
									inputProps={{ maxLength: 3 }}
								/>
							</>
							<Box style={{ marginTop: 12 }}>
								<QRColorPickerPopover
									name="glassTextColor"
									label="Drawer/Modal Text Color"
									color={glassTextColor ? `#${glassTextColor}` : '#212529'}
									setColor={(color: string) => {
										const hex = color.replace(/^#/, '');
										setGlassTextColor(hex);
										updateEnableButton({ glassTextColor: hex });
									}}
									setError={() => {}}
								/>
							</Box>
							<FormControlLabel
								control={
									<Switch
										checked={imageRound}
										onChange={(e) => {
											setImageRound(e.target.checked);
											updateEnableButton({ imageRound: e.target.checked });
										}}
										color="primary"
									/>
								}
								label="Round image corners"
								style={{ marginTop: 12 }}
							/>
						</>
					)}
				</Box>

				{/* App Details Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle} variant="subtitle2">
						APP DETAILS
					</Typography>
					<TextField
						className={`${classes.formField} ${classes.readOnlyField}`}
						label="App Name"
						value={formData.appName}
						disabled
						helperText="The name of your app"
						variant="outlined"
						fullWidth
					/>
				</Box>

				{/* Contact Information Section */}
				<Box className={classes.formSection}>
					<Typography className={classes.sectionTitle} variant="subtitle2">
						CONTACT INFORMATION
					</Typography>
					<TextField
						className={`${classes.formField} ${classes.readOnlyField}`}
						label="Contact Name"
						value={formData.contactName}
						disabled
						helperText="Primary contact name"
						variant="outlined"
						fullWidth
					/>
					<TextField
						className={`${classes.formField} ${classes.readOnlyField}`}
						label="Phone Number"
						value={formData.phoneNumber}
						disabled
						helperText="Contact phone number"
						variant="outlined"
						fullWidth
					/>
					<TextField
						className={`${classes.formField} ${classes.readOnlyField}`}
						label="Email"
						value={formData.email}
						disabled
						helperText="Contact email address"
						variant="outlined"
						fullWidth
					/>
				</Box>

				<Box className={classes.buttonBox}>
					<FlatButton className={classes.button} variant="contained" color="primary" onClick={handleCancel}>
						Cancel
					</FlatButton>
					<FlatButton
						disabled={!enableButton}
						className={classes.button}
						variant="contained"
						color="primary"
						onClick={handleSave}
					>
						Save
					</FlatButton>
				</Box>
			</Box>
		</ViiisionTempDrawer>
	);
};

export default AppInformationDrawer;
